import Fuse from "fuse.js";
import { Eye, Globe, Search, SortAsc, SortDesc, Target, Users, X } from "lucide-react";
import * as React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";
import { Button } from "@patient-watch/ui/button";
import { Label } from "@patient-watch/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@patient-watch/ui/drawer";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { cn } from "@patient-watch/ui";
import { isOwnedBuilderDraft } from "./ownedBuilderDraft";

interface FormOption {
    form_id: string;
    form_name: string;
    slug?: string;
    form_description?: string;
    prevalence?: string;
    scoring?: string;
    time_to_complete?: number;
    indications?: string[];
    orthopaedic_areas?: string[];
    number_of_pages?: number;
    number_of_questions?: number;
    is_published?: boolean;
    author?: string | null;
}

interface FormSelectDialogProps<T extends FieldValues> {
    name: Path<T>;
    control: Control<T>;
    options: FormOption[];
    addOption?: React.ReactNode;
    currentUserId?: string | null;
    label?: string;
    description?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
    /** Tighter spacing for dense dialogs (e.g. questionnaire lists). */
    compact?: boolean;
    /** App locale tag, e.g. "de-DE". Defaults to English copy. */
    locale?: string;
}

/** ponytail: inline copy - packages/forms has no i18n dependency, callers pass their locale. */
function dialogCopy(locale?: string) {
    if (locale === "de-DE") {
        return {
            placeholder: "Formular auswählen",
            title: "Fragebogen auswählen",
            search: "Formulare suchen…",
            sortLabel: (order: string) => `Sortierung: ${order}`,
            sortTooltip: (order: string) => `Alphabetisch sortieren (${order})`,
            empty: "Keine Formulare gefunden.",
            yours: "Deins",
            clear: "Löschen",
            questions: (count: number) => `${count} Fragen`,
            completionTime: "Typische Bearbeitungszeit",
            areas: "Orthopädische Bereiche",
            prevalence: "Wie verbreitet dieser Fragebogen in der Routinepraxis ist",
        };
    }
    if (locale === "fr-FR") {
        return {
            placeholder: "Sélectionner un formulaire",
            title: "Sélectionner un questionnaire",
            search: "Rechercher des formulaires…",
            sortLabel: (order: string) => `Tri : ${order}`,
            sortTooltip: (order: string) => `Trier par ordre alphabétique (${order})`,
            empty: "Aucun formulaire trouvé.",
            yours: "Le vôtre",
            clear: "Effacer",
            questions: (count: number) => `${count} questions`,
            completionTime: "Temps de réponse habituel",
            areas: "Zones orthopédiques",
            prevalence: "Fréquence d'utilisation de ce questionnaire en pratique courante",
        };
    }
    if (locale === "it-IT") {
        return {
            placeholder: "Seleziona un modulo",
            title: "Seleziona un questionario",
            search: "Cerca moduli…",
            sortLabel: (order: string) => `Ordine: ${order}`,
            sortTooltip: (order: string) => `Ordina alfabeticamente (${order})`,
            empty: "Nessun modulo trovato.",
            yours: "Il Suo",
            clear: "Cancella",
            questions: (count: number) => `${count} domande`,
            completionTime: "Tempo di compilazione abituale",
            areas: "Aree ortopediche",
            prevalence: "Quanto è usato questo questionario nella pratica clinica",
        };
    }
    if (locale === "es-ES") {
        return {
            placeholder: "Seleccionar un formulario",
            title: "Seleccionar un cuestionario",
            search: "Buscar formularios…",
            sortLabel: (order: string) => `Orden: ${order}`,
            sortTooltip: (order: string) => `Ordenar alfabéticamente (${order})`,
            empty: "No se han encontrado formularios.",
            yours: "El suyo",
            clear: "Borrar",
            questions: (count: number) => `${count} preguntas`,
            completionTime: "Tiempo habitual de cumplimentación",
            areas: "Áreas ortopédicas",
            prevalence: "Frecuencia de uso de este cuestionario en la práctica clínica",
        };
    }
    if (locale === "da-DK") {
        return {
            placeholder: "Vælg formular",
            title: "Vælg spørgeskema",
            search: "Søg i formularer…",
            sortLabel: (order: string) => `Sortering: ${order}`,
            sortTooltip: (order: string) => `Sortér alfabetisk (${order})`,
            empty: "Ingen formularer fundet.",
            yours: "Din",
            clear: "Ryd",
            questions: (count: number) => `${count} spørgsmål`,
            completionTime: "Typisk udfyldelsestid",
            areas: "Ortopædiske områder",
            prevalence: "Hvor almindeligt dette spørgeskema er i daglig praksis",
        };
    }
    return {
        placeholder: "Select a form",
        title: "Select a Questionnaire",
        search: "Search forms...",
        sortLabel: (order: string) => `Sort: ${order}`,
        sortTooltip: (order: string) => `Sort alphabetically (${order})`,
        empty: "No forms found.",
        yours: "Yours",
        clear: "Clear",
        questions: (count: number) => `${count} questions`,
        completionTime: "Typical completion time",
        areas: "Orthopaedic areas",
        prevalence: "How common this questionnaire is in routine practice",
    };
}

const prevalenceIcons: Record<string, React.ReactNode> = {
    ubiquitous: <Globe size={20} />,
    common: <Users size={20} />,
    uncommon: <Eye size={20} />,
    niche: <Target size={20} />,
};

/** Green through purple: `border-*-100`, `bg-*-50`, `text-*-800` */
const orthopaedicAreaBadgeClasses = [
    "border-green-100 bg-green-50 text-green-800",
    "border-emerald-100 bg-emerald-50 text-emerald-800",
    "border-teal-100 bg-teal-50 text-teal-800",
    "border-cyan-100 bg-cyan-50 text-cyan-800",
    "border-sky-100 bg-sky-50 text-sky-800",
    "border-blue-100 bg-blue-50 text-blue-800",
    "border-indigo-100 bg-indigo-50 text-indigo-800",
    "border-violet-100 bg-violet-50 text-violet-800",
    "border-purple-100 bg-purple-50 text-purple-800",
] as const;

function orthopaedicAreaBadgeClass(area: string): string {
    let h = 0;
    for (let i = 0; i < area.length; i++) {
        h = (Math.imul(31, h) + area.charCodeAt(i)) | 0;
    }
    return orthopaedicAreaBadgeClasses[Math.abs(h) % orthopaedicAreaBadgeClasses.length];
}

export function FormSelectDialog<T extends FieldValues>({
    name,
    control,
    options,
    addOption,
    currentUserId,
    label,
    description,
    placeholder,
    disabled,
    compact = false,
    locale,
}: FormSelectDialogProps<T>) {
    const copy = dialogCopy(locale);
    const {
        field: { value, onChange },
        fieldState: { error },
    } = useController({ name, control });
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [alphaAsc, setAlphaAsc] = React.useState(true);
    const isMobile = useMediaQuery("(max-width: 640px)");

    const fuse = React.useMemo(
        () =>
            new Fuse(options, {
                keys: ["form_name", "slug", "form_description", "indications", "orthopaedic_areas"],
                threshold: 0.4,
            }),
        [options]
    );

    const filtered = React.useMemo(() => {
        const searched = search ? fuse.search(search).map((result) => result.item) : options;
        return [...searched].sort((a, b) => {
            const ownedA = isOwnedBuilderDraft({
                authorId: a.author,
                currentUserId,
                isPublished: a.is_published,
            });
            const ownedB = isOwnedBuilderDraft({
                authorId: b.author,
                currentUserId,
                isPublished: b.is_published,
            });
            if (ownedA !== ownedB) return ownedA ? -1 : 1;
            const comparison = a.form_name.localeCompare(b.form_name, undefined, { sensitivity: "base" });
            return alphaAsc ? comparison : -comparison;
        });
    }, [alphaAsc, currentUserId, fuse, options, search]);

    function handleSelect(form_id: string) {
        onChange(form_id);
        setOpen(false);
    }

    function handleClear(event?: React.MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();
        onChange("");
        setOpen(false);
    }

    const selected = options.find((o) => o.form_id === value);

    const Content = (
        <div className={cn("flex flex-col w-full", compact ? "gap-1.5" : "gap-4")}>
            {(label || description) && (
                <div className="flex flex-col gap-0.5">
                    {label && <Label>{label}</Label>}
                    {description && <span className="text-slate-500 text-xs">{description}</span>}
                </div>
            )}
            <Button
                type="button"
                variant="outline"
                className={cn(
                    "justify-between bg-white px-3 border-slate-300 rounded-md focus-visible:ring-2 focus-visible:ring-indigo-300 ring-offset-white focus-visible:ring-offset-2 w-full min-w-0 overflow-hidden text-sm text-left",
                    compact ? "h-10" : "h-12",
                    !selected && "text-slate-500"
                )}
                onClick={() => setOpen(true)}
                disabled={disabled}
            >
                <span className="min-w-0 text-sm truncate">
                    {selected ? (
                        <span className="font-medium">{selected.form_name}</span>
                    ) : (
                        (placeholder ?? copy.placeholder)
                    )}
                </span>
                <Search className="ml-2 w-4 h-4" />
            </Button>
            {error && <span className="text-red-500 text-xs">{error.message}</span>}
        </div>
    );

    const OptionCard = (option: FormOption) => (
        <button
            key={option.form_id}
            type="button"
            data-testid={`form-option-${option.slug || option.form_name.toLowerCase().replace(/\s+/g, "-")}`}
            data-form-option="true"
            data-form-slug={option.slug}
            className={cn(
                "relative flex flex-col gap-2 hover:bg-slate-50 dark:hover:bg-slate-900 p-4 border rounded-lg w-full text-left transition",
                value === option.form_id && "border-indigo-600 ring-2 ring-indigo-100 shadow-sm bg-indigo-50/40"
            )}
            onClick={() => handleSelect(option.form_id)}
        >
            <div className="flex flex justify-between gap-2">
                <div className="flex flex-col gap-2">
                    <div className={cn("font-semibold text-base", value === option.form_id && "text-indigo-700")}>
                        {option.form_name}
                    </div>
                    {isOwnedBuilderDraft({
                        authorId: option.author,
                        currentUserId,
                        isPublished: option.is_published,
                    }) ? (
                        <span
                            className="inline-flex w-fit items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-medium text-indigo-800 text-xs"
                            data-testid="builder-yours-badge"
                        >
                            {copy.yours}
                        </span>
                    ) : null}
                    {option.number_of_questions ? (
                        <span className="text-slate-500 text-xs" title={copy.completionTime}>
                            {copy.questions(option.number_of_questions)}
                        </span>
                    ) : null}
                    {option.orthopaedic_areas && option.orthopaedic_areas.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5" title={copy.areas}>
                            {option.orthopaedic_areas.map((area) => (
                                <span
                                    key={area}
                                    className={cn(
                                        "inline-flex items-center px-2.5 py-0.5 border rounded-full font-medium text-xs capitalize",
                                        orthopaedicAreaBadgeClass(area)
                                    )}
                                >
                                    {area}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {option.prevalence ? (
                        <span
                            className={cn(
                                "inline-flex items-center gap-1 shadow-sm px-2 py-1 border rounded-md font-semibold text-[0.8rem]",
                                option.prevalence === "ubiquitous"
                                    ? "bg-green-100 text-green-800 border-green-200"
                                    : option.prevalence === "common"
                                      ? "bg-blue-100 text-blue-800 border-blue-200"
                                      : option.prevalence === "uncommon"
                                        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                        : "bg-gray-100 text-gray-700 border-gray-200"
                            )}
                            title={copy.prevalence}
                        >
                            {prevalenceIcons[option.prevalence]}
                            <span className="capitalize">{option.prevalence}</span>
                        </span>
                    ) : null}
                </div>
            </div>
        </button>
    );

    const sortOrder = alphaAsc ? "A-Z" : "Z-A";
    const sortLabel = copy.sortLabel(sortOrder);
    const sortTooltip = copy.sortTooltip(sortOrder);

    const List = (
        <>
            <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder={copy.search}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm"
                    />
                    <Search className="top-1/2 right-2 absolute w-4 h-4 text-slate-500 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setAlphaAsc((asc) => !asc)}
                        title={sortTooltip}
                        aria-label={sortTooltip}
                    >
                        {alphaAsc ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
                    </Button>
                    <span className="min-w-[80px] text-slate-500 text-xs" title={sortTooltip}>
                        {sortLabel}
                    </span>
                </div>
            </div>
            <div className="flex-1 -mx-1 px-1 min-h-0 max-h-[60vh] overflow-y-auto">
                <div className="flex flex-col gap-2">
                    {filtered.length === 0 && <div className="py-8 text-slate-400 text-center">{copy.empty}</div>}
                    {filtered.map(OptionCard)}
                </div>
            </div>
            <div className="flex items-center gap-2 mt-2 pt-2 border-slate-200 border-t">
                {addOption ? <div className="flex flex-1 min-w-0">{addOption}</div> : null}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={handleClear}
                    disabled={value == null || value === ""}
                >
                    <X className="w-4 h-4" />
                    {copy.clear}
                </Button>
            </div>
        </>
    );

    return (
        <>
            {Content}
            {isMobile ? (
                <Drawer open={open} onOpenChange={setOpen}>
                    <DrawerContent>
                        <DrawerHeader>
                            <DrawerTitle>{copy.title}</DrawerTitle>
                        </DrawerHeader>
                        <div className="p-4">{List}</div>
                    </DrawerContent>
                </Drawer>
            ) : (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogContent className="flex flex-col p-4 md:p-6 max-w-lg max-h-[85vh] overflow-hidden">
                        <DialogHeader className="shrink-0">
                            <DialogTitle>{copy.title}</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col flex-1 gap-4 min-h-0">{List}</div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
